import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AddOutlined,
  CheckCircleOutlined,
  ContentCutOutlined,
  EditOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { groomingService } from "../../services";

const SEARCH_DEBOUNCE_MS = 350;

const GRID_SX = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#757B2B",
    color: "#ffffff",
  },
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
    backgroundColor: "#757B2B !important",
  },
  "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
  {
    color: "#ffffff !important",
  },
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

const buildForm = (row = null) => ({
  name: String(row?.name || ""),
  suggested_duration_minutes:
    row?.suggested_duration_minutes === 0 || row?.suggested_duration_minutes
      ? String(row.suggested_duration_minutes)
      : "60",
  base_price: row?.base_price === 0 || row?.base_price ? String(row.base_price) : "0.00",
  is_active: row?.is_active !== false,
});

function GroomingServiceTypesPage() {
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canManage = hasPermission(effectivePerms, "grooming_settings_manage", isSuperuser);
  const canViewPage = canManage;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [dialog, setDialog] = useState({ open: false, mode: "create", row: null });
  const [form, setForm] = useState(buildForm(null));
  const [saving, setSaving] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadRows = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await groomingService.listServiceTypes({
        branch_id: branchId,
        q: debouncedSearch || undefined,
        active: activeOnly ? "true" : "false",
      });
      setRows(toArray(data));
    } catch (error) {
      setRows([]);
      showSnack(extractErrorMessage(error, "No se pudieron cargar los tipos de servicio."), "error");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, branchId, canViewPage, debouncedSearch, showSnack]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const closeDialog = useCallback(() => {
    if (saving) return;
    setDialog({ open: false, mode: "create", row: null });
    setForm(buildForm(null));
  }, [saving]);

  const openCreateDialog = useCallback(() => {
    setDialog({ open: true, mode: "create", row: null });
    setForm(buildForm(null));
  }, []);

  const openEditDialog = useCallback((row) => {
    setDialog({ open: true, mode: "edit", row });
    setForm(buildForm(row));
  }, []);

  const handleSave = useCallback(async () => {
    if (!branchId || !canManage) return;
    const name = String(form.name || "").trim();
    const duration = Number(form.suggested_duration_minutes);
    const basePrice = Number(form.base_price);
    if (!name) {
      showSnack("Debes ingresar el nombre del tipo de servicio.", "warning");
      return;
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      showSnack("La duracion sugerida debe ser mayor que cero.", "warning");
      return;
    }
    if (!Number.isFinite(basePrice) || basePrice <= 0) {
      showSnack("La tarifa base debe ser mayor que cero.", "warning");
      return;
    }

    setSaving(true);
    try {
      if (dialog.mode === "edit" && dialog.row?.id) {
        await groomingService.updateServiceType(dialog.row.id, {
          branch_id: branchId,
          name,
          suggested_duration_minutes: Math.round(duration),
          base_price: basePrice.toFixed(2),
          is_active: Boolean(form.is_active),
        });
        showSnack("Tipo de servicio actualizado.", "success");
      } else {
        await groomingService.createServiceType({
          branch_id: branchId,
          name,
          suggested_duration_minutes: Math.round(duration),
          base_price: basePrice.toFixed(2),
          is_active: Boolean(form.is_active),
        });
        showSnack("Tipo de servicio creado.", "success");
      }
      closeDialog();
      await loadRows();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo guardar el tipo de servicio."), "error");
    } finally {
      setSaving(false);
    }
  }, [branchId, canManage, closeDialog, dialog.mode, dialog.row?.id, form.base_price, form.is_active, form.name, form.suggested_duration_minutes, loadRows, showSnack]);

  const handleToggleStatus = useCallback(
    async (row) => {
      if (!branchId || !canManage || !row?.id) return;
      try {
        await groomingService.updateServiceType(row.id, {
          branch_id: branchId,
          is_active: !row.is_active,
        });
        showSnack(row.is_active ? "Tipo de servicio desactivado." : "Tipo de servicio activado.", "success");
        await loadRows();
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo actualizar el estado."), "error");
      }
    },
    [branchId, canManage, loadRows, showSnack]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "name", headerName: "Nombre", flex: 1, minWidth: 220 },
      {
        field: "suggested_duration_minutes",
        headerName: "Duracion sugerida",
        minWidth: 170,
        flex: 0.6,
        valueGetter: (_, row) => `${row?.suggested_duration_minutes || 60} min`,
      },
      {
        field: "base_price",
        headerName: "Tarifa base",
        minWidth: 130,
        flex: 0.5,
        valueGetter: (_, row) => `Q${Number(row?.base_price || 0).toFixed(2)}`,
      },
      {
        field: "is_active",
        headerName: "Estado",
        minWidth: 130,
        flex: 0.6,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row?.is_active ? "Activo" : "Inactivo"}
            color={row?.is_active ? "success" : "default"}
            variant={row?.is_active ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        minWidth: 130,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <span>
                <IconButton size="small" onClick={() => openEditDialog(row)} disabled={!canManage}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row?.is_active ? "Desactivar" : "Activar"}>
              <span>
                <IconButton size="small" onClick={() => handleToggleStatus(row)} disabled={!canManage}>
                  <CheckCircleOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canManage, handleToggleStatus, openEditDialog]
  );

  if (!canViewPage) {
    return <Alert severity="warning">No tienes permisos para ver grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para operar grooming.</Alert>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <MainText
        title="Tipos de servicio grooming"
        subtitle="Catalogo operativo para agendar citas"
        icon={ContentCutOutlined}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
          <TextField
            label="Buscar"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: <SearchOutlined sx={{ mr: 1, color: "text.secondary" }} fontSize="small" />,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
              />
            }
            label={activeOnly ? "Activos" : "Inactivos"}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: "auto" } }}>
            <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
              Actualizar
            </Button>
            <Button variant="contained" startIcon={<AddOutlined />} disabled={!canManage} onClick={openCreateDialog}>
              Crear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 290px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 20 } } }}
          sx={GRID_SX}
        />
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {dialog.mode === "edit" ? "Editar tipo de servicio" : "Crear tipo de servicio"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Duracion sugerida (min)"
              type="number"
              value={form.suggested_duration_minutes}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, suggested_duration_minutes: event.target.value }))
              }
              inputProps={{ min: 1, step: 5 }}
              fullWidth
              size="small"
            />
            <TextField
              label="Tarifa base"
              type="number"
              value={form.base_price}
              onChange={(event) => setForm((prev) => ({ ...prev, base_price: event.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.is_active)}
                  onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
              }
              label={form.is_active ? "Activo" : "Inactivo"}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : dialog.mode === "edit" ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default GroomingServiceTypesPage;
